import {
  WidgetDataResult,
  WordCloudConfig,
  WordCloudMode,
  ColumnMapping,
} from '../types';

export type WordCloudKind = 'keyword' | 'emoji';

export type TrendDirection = 'rising' | 'falling' | 'steady';

export interface WordTrend {
  /** Relative change against the previous period: 0.4 = +40%. */
  change: number;
  direction: TrendDirection;
}

export type SentimentLabel = 'positive' | 'negative' | 'neutral';

export interface WordSentiment {
  /** Net sentiment from -1 (all negative) to 1 (all positive); null when the SQL gave a label. */
  score: number | null;
  label: SentimentLabel;
}

export interface WordCloudItem {
  name: string;
  value: number;
  kind: WordCloudKind;
  /** Present only when the widget has a trend column and the row holds a number. */
  trend?: WordTrend;
  /** Present only when the widget has a sentiment column and the row holds a value. */
  sentiment?: WordSentiment;
  /** The original SQL row, exposed through click actions as {column_name} placeholders. */
  row: Record<string, unknown>;
}

export const WORD_CLOUD_DEFAULTS = {
  mode: 'both' as WordCloudMode,
  maxWords: 100,
  minWordLength: 2,
  shape: 'circle' as NonNullable<WordCloudConfig['shape']>,
  trendThreshold: 0.3,
  sentimentThreshold: 0.15,
  gridSize: 8,
};

// Variation selectors, zero-width joiner and skin-tone modifiers glue emoji
// sequences together; they are not glyphs on their own.
const EMOJI_JOINERS = /[\u{FE0E}\u{FE0F}\u{200D}]|[\u{1F3FB}-\u{1F3FF}]/gu;
// A pictograph, a regional-indicator (flag half) or the combining keycap mark (1️⃣).
const EMOJI_GLYPH =
  /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u{20E3}/u;
const EMOJI_ONLY =
  /^(?:\p{Extended_Pictographic}|\p{Regional_Indicator}|\u{20E3}|\p{N}|\s)+$/u;

/**
 * True when a token is made of emoji only: pictographs (😀, ❤️, 🇺🇦) with no
 * letters in it. Digits are allowed so keycaps such as 1️⃣ count as emoji.
 */
export function isEmojiToken(token: string): boolean {
  const core = token.replace(EMOJI_JOINERS, '').trim();
  if (!core) return false;
  if (/\p{L}/u.test(core)) return false;
  return EMOJI_ONLY.test(core) && EMOJI_GLYPH.test(core);
}

function resolveKind(explicit: unknown, token: string): WordCloudKind {
  if (typeof explicit === 'string') {
    const k = explicit.trim().toLowerCase();
    if (k === 'emoji' || k === 'emojis') return 'emoji';
    if (k === 'keyword' || k === 'keywords' || k === 'word') return 'keyword';
  }
  return isEmojiToken(token) ? 'emoji' : 'keyword';
}

/**
 * A token rises when it grew by at least `threshold`, and falls when it shrank by the
 * same factor (30% up is x1.3, so 30% "down" is /1.3, about -23%). Anything in between
 * is steady, which keeps sampling noise from painting the whole cloud.
 */
export function classifyTrend(
  change: number,
  threshold: number = WORD_CLOUD_DEFAULTS.trendThreshold,
): TrendDirection {
  const ratio = 1 + change;
  if (ratio >= 1 + threshold) return 'rising';
  if (ratio <= 1 / (1 + threshold)) return 'falling';
  return 'steady';
}

/**
 * Reads a sentiment cell: a net score from -1 to 1 (positive minus negative share),
 * or a ready label such as "NEGATIVE". Scores inside the threshold are neutral.
 */
export function parseSentiment(
  raw: unknown,
  threshold: number = WORD_CLOUD_DEFAULTS.sentimentThreshold,
): WordSentiment | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (typeof raw === 'string') {
    const key = raw.trim().toLowerCase();
    if (key === 'positive' || key === 'negative' || key === 'neutral') {
      return { score: null, label: key };
    }
  }
  const score = Number(raw);
  if (!Number.isFinite(score)) return undefined;
  if (score >= threshold) return { score, label: 'positive' };
  if (score <= -threshold) return { score, label: 'negative' };
  return { score, label: 'neutral' };
}

/** "+42%", "−30%", "0%". Capped so one tiny baseline does not print "+4200%". */
export function formatTrend(change: number): string {
  const pct = Math.round(change * 100);
  if (pct > 999) return '+999%+';
  if (pct > 0) return `+${pct}%`;
  // U+2212 minus: the same width as "+", unlike a hyphen.
  if (pct < 0) return `${String.fromCharCode(0x2212)}${Math.abs(pct)}%`;
  return '0%';
}

/** The token as hidden-word lists store it: keywords ignore case, emojis are exact. */
export function hiddenKey(name: string): string {
  return name.trim().toLowerCase();
}

/** Number of messages behind the counts, read from the first row of `sampleColumn`. */
export function readSampleSize(
  data: WidgetDataResult,
  config: WordCloudConfig | undefined,
): number | null {
  const col = config?.sampleColumn;
  if (!col || !data.rows.length) return null;
  const n = Number(data.rows[0][col]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Turns SQL rows into typed cloud items; drops empty names and non-positive values. */
export function buildWordCloudItems(
  data: WidgetDataResult,
  mapping: ColumnMapping | undefined,
  config: WordCloudConfig | undefined,
): WordCloudItem[] {
  const nameCol = mapping?.name || data.columns[0];
  const valueCol = mapping?.value || data.columns[1] || data.columns[0];
  const kindCol = config?.kindColumn;
  const trendCol = config?.trendColumn;
  const threshold = config?.trendThreshold;
  const sentimentCol = config?.sentimentColumn;

  const items: WordCloudItem[] = [];
  for (const row of data.rows) {
    const name = String(row[nameCol] ?? '').trim();
    const value = Number(row[valueCol]);
    if (!name || !Number.isFinite(value) || value <= 0) continue;
    const rawTrend = trendCol ? row[trendCol] : null;
    const change =
      rawTrend === null || rawTrend === undefined || rawTrend === ''
        ? NaN
        : Number(rawTrend);
    items.push({
      name,
      value,
      kind: resolveKind(kindCol ? row[kindCol] : undefined, name),
      trend: Number.isFinite(change)
        ? { change, direction: classifyTrend(change, threshold) }
        : undefined,
      sentiment: sentimentCol
        ? parseSentiment(row[sentimentCol], config?.sentimentThreshold)
        : undefined,
      row,
    });
  }
  return items;
}

/**
 * Applies the mode, stop-word, hidden-word, length and max-words rules and sorts by
 * value descending. `hidden` holds hiddenKey() values the viewer dismissed.
 */
export function filterWordCloudItems(
  items: WordCloudItem[],
  mode: WordCloudMode,
  config: WordCloudConfig | undefined,
  hidden?: ReadonlySet<string>,
): WordCloudItem[] {
  const minLen = config?.minWordLength ?? WORD_CLOUD_DEFAULTS.minWordLength;
  const maxWords = config?.maxWords ?? WORD_CLOUD_DEFAULTS.maxWords;
  const stop = new Set(
    (config?.stopWords ?? [])
      .map((w) => w.trim().toLowerCase())
      .filter(Boolean),
  );

  return items
    .filter((item) => {
      if (mode === 'keywords' && item.kind !== 'keyword') return false;
      if (mode === 'emojis' && item.kind !== 'emoji') return false;
      if (hidden?.has(hiddenKey(item.name))) return false;
      if (item.kind === 'keyword') {
        if (item.name.length < minLen) return false;
        if (stop.has(item.name.toLowerCase())) return false;
      }
      return true;
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, Math.max(1, maxWords));
}

/**
 * The one text colour of frequency mode. Size already encodes frequency, so colour
 * carries nothing there: a ramp would show the same number twice. Keeps 4.5:1 on a
 * white card at the smallest font size.
 */
export const WORD_CLOUD_COLOR = '#1d4ed8';

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

const FONT_WEIGHT = 600;
/** Share of the card the word boxes may cover; the spiral packing wastes the rest. */
const FILL_RATIO = 0.8;
const MIN_FONT = 12;

/**
 * Every word is drawn at this fraction of the size it was laid out at, shrunk
 * around its own centre. The plugin packs words on a pixel grid that allows a zero
 * gap between neighbours, so two words can read as one ("війнисловами"); the
 * shrink leaves each word 5% of its own size as clear space on every side.
 * Applied by ChartWordCloud after each render — the plugin has no option for it.
 */
export const WORD_DRAW_SCALE = 0.9;

/** 1 = most frequent, 0 = least, on a square-root scale (see buildWordCloudSeries). */
function relativeWeights(items: WordCloudItem[]): number[] {
  const roots = items.map((item) => Math.sqrt(item.value));
  const lo = Math.min(...roots);
  const span = Math.max(...roots) - lo;
  // A cloud where every value is equal is all "1".
  return roots.map((r) => (span > 0 ? (r - lo) / span : 1));
}

/**
 * Smallest font size an emoji is laid out at. A word stays readable at 12px, but an
 * emoji is a single detailed glyph, and next to keywords its count is usually far
 * lower, so without a floor every emoji lands on the minimum and turns into a speck.
 * Scales with the cloud, between 20 and 32px, and never exceeds the top size.
 */
export function emojiFloor(
  min: number,
  max: number,
  minEmojiSize?: number,
): number {
  const wanted = minEmojiSize ?? clamp(max * 0.45, 20, 32);
  return Math.round(clamp(wanted, min, Math.max(min, max)));
}

/** Font size per item: linear in its weight, with emojis lifted to their floor. */
function itemFontSizes(
  items: WordCloudItem[],
  weights: number[],
  min: number,
  max: number,
  minEmojiSize?: number,
): number[] {
  const floor = emojiFloor(min, max, minEmojiSize);
  return items.map((item, i) => {
    const size = min + (max - min) * weights[i];
    return item.kind === 'emoji' ? Math.max(size, floor) : size;
  });
}

const EMOJI_PRESENTATION = String.fromCharCode(0xfe0f);

/**
 * The token as it is drawn. Symbols such as a check mark, crossed swords or a heart
 * exist both as a thin text glyph and as a colour emoji; the SQL returns the bare
 * code point, which fonts draw as the text glyph. The emoji variation selector asks
 * for the colour one. Symbols without an emoji form ignore it.
 */
export function displayName(item: WordCloudItem): string {
  if (item.kind !== 'emoji' || item.name.includes(EMOJI_PRESENTATION)) {
    return item.name;
  }
  const points = Array.from(item.name);
  const code = points[0].codePointAt(0) ?? 0;
  return points.length === 1 && code < 0x1f000
    ? `${item.name}${EMOJI_PRESENTATION}`
    : item.name;
}

/** Rough advance width of a token in em: one emoji glyph, or ~0.6em per letter. */
function widthInEm(item: WordCloudItem): number {
  return item.kind === 'emoji' ? 1.3 : Array.from(item.name).length * 0.6;
}

/**
 * Picks the largest font size range whose words still fit the card, so the cloud
 * fills a big widget and stays legible in a small one, whether it holds 10 words
 * or 100. The smallest word never drops below a readable 12px.
 */
export function fitSizeRange(
  items: WordCloudItem[],
  width: number,
  height: number,
  gridSize: number,
  fillRatio = FILL_RATIO,
  minEmojiSize?: number,
): [number, number] {
  const weights = relativeWeights(items);
  const budget = width * height * fillRatio;
  // The top word may take up to a third of the card height.
  const ceiling = Math.round(clamp(height / 3.2, 28, 110));

  const fits = (min: number, max: number) => {
    const sizes = itemFontSizes(items, weights, min, max, minEmojiSize);
    let area = 0;
    let widest = 0;
    items.forEach((item, i) => {
      const w = widthInEm(item) * sizes[i] + gridSize;
      area += w * (sizes[i] * 1.2 + gridSize);
      widest = Math.max(widest, w);
    });
    return area <= budget && widest <= width * 0.8;
  };

  for (let max = ceiling; max > MIN_FONT; max -= 2) {
    let min = Math.round(clamp(max / 4.5, MIN_FONT, 20));
    if (!fits(min, max)) continue;
    // A sparse cloud (a dozen words, or emojis only) still has room: lift the small
    // end, up to half the top size, so the card is filled instead of left empty.
    while (min + 2 <= max / 2 && fits(min + 2, max)) min += 2;
    return [min, max];
  }
  return [MIN_FONT, MIN_FONT + 4];
}

/**
 * Trend colours: blue for rising, red for falling, grey for steady (design review:
 * falling reads as red). Blue and red stay apart for colour-blind readers, and the
 * list view and tooltip repeat the direction as an arrow. All three keep 4.5:1 on a
 * white card.
 */
export const TREND_COLORS: Record<TrendDirection, string> = {
  rising: '#1d4ed8',
  falling: '#b91c1c',
  steady: '#475569',
};

/**
 * Sentiment colours for word text. The product's sentiment green, yellow and red
 * are fill colours; as text on white the yellow and the red are unreadable, so these
 * are darker steps of the same hues with a neutral grey in the middle. All keep
 * 4.5:1 on a white card and pass the colour-blind separation check.
 */
export const SENTIMENT_WORD_COLORS: Record<SentimentLabel, string> = {
  positive: '#15803d',
  neutral: '#475569',
  negative: '#b91c1c',
};

export type WordCloudColorBy = 'frequency' | 'trend' | 'sentiment';

/** Colour encodings the data can support, in the order they are offered to viewers. */
export function availableColorModes(
  items: WordCloudItem[],
): WordCloudColorBy[] {
  const modes: WordCloudColorBy[] = ['frequency'];
  if (items.some((item) => item.trend)) modes.push('trend');
  if (items.some((item) => item.sentiment)) modes.push('sentiment');
  return modes;
}

/**
 * What colour encodes. The viewer's choice wins, then the config, then the richest
 * encoding the data carries (trend, then sentiment); frequency is always available.
 */
export function resolveColorBy(
  items: WordCloudItem[],
  config: WordCloudConfig | undefined,
  chosen?: WordCloudColorBy | null,
): WordCloudColorBy {
  const available = availableColorModes(items);
  if (chosen && available.includes(chosen)) return chosen;
  if (config?.colorBy && available.includes(config.colorBy))
    return config.colorBy;
  if (config?.colorBy === 'frequency') return 'frequency';
  if (available.includes('trend')) return 'trend';
  if (available.includes('sentiment')) return 'sentiment';
  return 'frequency';
}

/**
 * One colour per item, shared by the cloud and the list so a word looks the same in
 * both. In frequency mode every word gets the first `colors` entry: size is the
 * frequency cue, so colour stays flat instead of encoding the same value twice.
 */
export function getWordColors(
  items: WordCloudItem[],
  colors: string[],
  colorBy: WordCloudColorBy,
): string[] {
  if (colorBy === 'trend') {
    return items.map((item) => TREND_COLORS[item.trend?.direction ?? 'steady']);
  }
  if (colorBy === 'sentiment') {
    return items.map(
      (item) => SENTIMENT_WORD_COLORS[item.sentiment?.label ?? 'neutral'],
    );
  }
  const single = colors[0] ?? WORD_CLOUD_COLOR;
  return items.map(() => single);
}

export interface WordCloudSeriesOptions {
  width: number;
  height: number;
  /** A concrete family list. Never 'inherit' — see buildWordCloudSeries. */
  fontFamily: string;
  /** Ramp from most to least frequent. */
  colors: string[];
  /** Default: 'frequency'. */
  colorBy?: WordCloudColorBy;
  config: WordCloudConfig | undefined;
}

/**
 * Builds the ECharts `wordCloud` series.
 *
 * Two things here are load-bearing:
 * - `fontFamily` must be a real family. The plugin measures every word on a hidden
 *   canvas with `ctx.font = weight size family`; an invalid value such as 'inherit'
 *   makes the canvas keep its 10px default, every word is measured as tiny, and the
 *   words are then drawn at full size on top of each other.
 * - Sizes use the square root of the value, so a word's area (what the eye compares)
 *   is proportional to its frequency and one dominant word does not flatten the rest
 *   to the minimum size. Emojis get their own floor (see emojiFloor). The real count
 *   travels in `raw` for tooltips; `value` holds the font size.
 */
export function buildWordCloudSeries(
  items: WordCloudItem[],
  {
    width,
    height,
    fontFamily,
    colors,
    colorBy = 'frequency',
    config,
  }: WordCloudSeriesOptions,
): Record<string, unknown> {
  const gridSize = config?.gridSize ?? WORD_CLOUD_DEFAULTS.gridSize;
  const itemColors = getWordColors(items, colors, colorBy);

  const [minSize, maxSize] =
    config?.sizeRange ??
    fitSizeRange(
      items,
      width,
      height,
      gridSize,
      FILL_RATIO,
      config?.minEmojiSize,
    );
  // The plugin maps values to sizes linearly and offers no per-item minimum, so the
  // font size itself is passed as the value and mapped onto its own extent.
  const sizes = itemFontSizes(
    items,
    relativeWeights(items),
    minSize,
    maxSize,
    config?.minEmojiSize,
  );

  const data = items.map((item, i) => ({
    name: displayName(item),
    value: sizes[i],
    raw: item.value,
    kind: item.kind,
    trend: item.trend,
    sentiment: item.sentiment,
    textStyle: { color: itemColors[i] },
  }));

  return {
    type: 'wordCloud',
    shape: config?.shape ?? WORD_CLOUD_DEFAULTS.shape,
    left: 'center',
    top: 'center',
    width: '96%',
    height: '94%',
    sizeRange: sizes.length
      ? [Math.min(...sizes), Math.max(...sizes)]
      : [minSize, maxSize],
    rotationRange: config?.rotate ? [-90, 90] : [0, 0],
    rotationStep: 90,
    gridSize,
    drawOutOfBound: false,
    shrinkToFit: true,
    // One synchronous pass: words appear together instead of popping in one by one.
    layoutAnimation: false,
    textStyle: { fontFamily, fontWeight: FONT_WEIGHT },
    data,
  };
}
